import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GiftRedemptionPage } from './components/GiftRedemptionPage';
import { GiftCodeEntryPage } from './components/GiftCodeEntryPage';
import { ShoppingProvider, useShopping } from './contexts/ShoppingContext';
import { CheckoutProvider } from './contexts/CheckoutContext';
import { UserProvider } from './contexts/UserContext';
import { ClientDataProvider } from './contexts/ClientDataContext';
import { Header } from './components/Header';
import { CategoryNav } from './components/CategoryNav';
import { ProductGrid } from './components/ProductGrid';
import { Cart } from './components/Cart';
import { CartPage } from './components/CartPage';
import { QuickViewModal } from './components/QuickViewModal';
import { ProductPage } from './components/ProductPage';
import { Checkout } from './components/Checkout';
import { OrderConfirmation } from './components/OrderConfirmation';
import { Toast } from './components/ui';
import { HelpButton } from './components/HelpButton';
import { ThemeDebugOverlay } from './components/ThemeDebugOverlay';
import { FeedbackButton } from './components/FeedbackButton';
import { FeedbackNudge } from './components/FeedbackNudge';
import { SanctumService } from './services/sanctumService';
import { usePreviewMode } from './hooks/usePreviewMode';
import { useGuestMode } from './hooks/useGuestMode';
import { useClientDataContext } from './contexts/ClientDataContext';
import { gtm } from './services/gtm';

const authCheckoutEnabled = import.meta.env.VITE_AUTH_CHECKOUT_ENABLED === 'true';

function AppContent() {
  const { state, dispatch } = useShopping();
  const location = useLocation();
  const { isPreviewMode } = usePreviewMode();
  const { clientData } = useClientDataContext();

  useEffect(() => {
    gtm.init(clientData?.id);
    if (clientData?.id != null) {
      gtm.pushClientId(clientData.id);
    }
  }, [clientData?.id]);

  useEffect(() => {
    gtm.pushPageView(location.pathname);
  }, [location.pathname]);
  const { isGuest, loading: userLoading } = useGuestMode();

  const isHomePage = location.pathname === '/';
  const homeScrollRef = useRef<HTMLDivElement>(null);
  const prevPathnameRef = useRef(location.pathname);

  // Clear saved scroll on full page load — only restore during SPA back-navigation
  useEffect(() => {
    localStorage.removeItem('homeScrollTop');
  }, []);

  useEffect(() => {
    const el = homeScrollRef.current;
    if (!el) return;
    const onScroll = () => localStorage.setItem('homeScrollTop', String(el.scrollTop));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Restore scroll only when navigating BACK from a product page — not on filter changes or
  // any other navigation to home.
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (location.pathname !== '/') return;
    if (!prev.startsWith('/product/') && prev !== '/cart') return;

    const el = homeScrollRef.current;
    if (!el) return;
    const saved = parseInt(localStorage.getItem('homeScrollTop') || '0', 10);
    if (!saved) return;
    // Single rAF — avoids a loop that could fire after data loads and trigger page 2.
    requestAnimationFrame(() => { el.scrollTop = saved; });
  }, [location.pathname]);

  const urlSearchParams = new URLSearchParams(location.search);
  const hasSelectedDepartment = !!urlSearchParams.get('departmentId');
  const selectedDepartmentId = urlSearchParams.get('departmentId');
  const selectedCategoryId = urlSearchParams.get('categoryId');
  const searchQuery = urlSearchParams.get('search');

  // Save last catalog URL so "Continue Shopping" can return to it
  useEffect(() => {
    if (isHomePage) {
      localStorage.setItem('lastCatalogSearch', location.search);
    }
  }, [isHomePage, location.search]);

  // Track previous pathname at render time so the reset effect can distinguish
  // "user changed filters while on home page" from "navigated away/back".
  const filterResetPrevPathRef = useRef(location.pathname);
  const filterResetPrevPath = filterResetPrevPathRef.current;
  filterResetPrevPathRef.current = location.pathname;

  // Reset scroll only when the user changes filters while STAYING on the home page.
  // Must not fire on navigation away (clears localStorage) or on return (resets scroll).
  useEffect(() => {
    if (location.pathname !== '/' || filterResetPrevPath !== '/') return;
    const el = homeScrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    localStorage.removeItem('homeScrollTop');
  }, [selectedDepartmentId, selectedCategoryId, searchQuery, filterResetPrevPath, location.pathname]);
  const showCategoryNav = !['/cart', '/checkout'].includes(location.pathname) &&
    !location.pathname.startsWith('/order-confirmation') &&
    (!isHomePage || hasSelectedDepartment);
  // ml-56 offset when sidebar is actually visible (departmentId in URL)
  const hasSidebarOffset = hasSelectedDepartment;

  return (
    <div className="h-dvh bg-gray-50 flex flex-col overflow-clip" style={{ paddingTop: 'var(--header-height, 108px)' }}>
      <Header />
      <div className="flex flex-1 min-h-0">
        {showCategoryNav && <CategoryNav />}
        <main id="main-content" role="main" className={`flex flex-col flex-1 min-h-0 ${hasSidebarOffset ? 'md:ml-56' : ''}`}>
          <div
            ref={homeScrollRef}
            data-product-scroll
            className="flex-1 overflow-y-auto min-h-0 touch-pan-y overscroll-y-contain"
            onTouchStart={() => {
              const el = document.activeElement;
              if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.blur();
            }}
            style={{ display: isHomePage ? undefined : 'none' }}
          >
            <ProductGrid />
          </div>

          <div
            className="flex-1 overflow-y-auto min-h-0 touch-pan-y overscroll-y-contain"
            style={{ display: isHomePage ? 'none' : undefined }}
          >
            <Routes>
              <Route index element={null} />
              <Route path="/product/:productId" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route
                path="/checkout"
                element={
                  (!isGuest && !authCheckoutEnabled && !userLoading) ? <Navigate to="/cart" replace /> :
                  <CheckoutProvider>
                    <Checkout />
                  </CheckoutProvider>
                }
              />
              <Route path="/order-confirmation/:masterOrderNo" element={<OrderConfirmation />} />
            </Routes>
          </div>
        </main>
      </div>
      <Cart />
      <QuickViewModal />
      <FeedbackNudge isPreviewMode={isPreviewMode} />

      <div className="fixed bottom-6 right-6 z-50 flex flex-col md:flex-row items-center gap-2">
        <ThemeDebugOverlay />
        <FeedbackButton/>
        <HelpButton />
      </div>

      {state.toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          productName={toast.productName}
          productImage={toast.productImage}
          type={toast.type}
          onClose={() => dispatch({ type: 'REMOVE_TOAST', payload: toast.id })}
        />
      ))}
    </div>
  );
}

function App() {
  // Initialize CSRF token when app loads
  // This fetches the XSRF-TOKEN cookie from Laravel Sanctum which is required
  // for all POST/PUT/DELETE requests to prevent CSRF attacks (419 errors)
  useEffect(() => {
    SanctumService.initCsrf();
  }, []);

  return (
    <Routes>
      {/* Standalone, no-chrome flow (PT-2616) - deliberately outside the storefront's
          Header/Cart/providers tree, since a gift claim link never touches the cart. The
          /preview/:programId variant is nova-kit's "Preview" button (see ProgramHeader.vue) -
          same component, in a non-submittable preview mode. /self/:selfProgramId is the
          self-service claim-code flow (see GiftCodeEntryPage.tsx) - same component, but
          submittable, and asks for the claimant's own email since no recipient exists yet. */}
      <Route path="/gift/preview/:programId" element={<GiftRedemptionPage />} />
      <Route path="/gift/self/:selfProgramId" element={<GiftRedemptionPage />} />
      <Route path="/gift/:recipientId" element={<GiftRedemptionPage />} />
      <Route path="/gift-code" element={<GiftCodeEntryPage />} />
      <Route
        path="*"
        element={
          <UserProvider>
            <ShoppingProvider>
              <ClientDataProvider>
                <AppContent />
              </ClientDataProvider>
            </ShoppingProvider>
          </UserProvider>
        }
      />
    </Routes>
  );
}

export default App;